/**
 * Shim de compatibilidade: next/link -> react-router-dom
 *
 * Usado por ~48 arquivos. O padrao do template e:
 *   <NextLink href={...} passHref><Button/></NextLink>
 *
 * O `passHref` do Next repassava o href ao filho; no react-router o proprio
 * <Link> ja renderiza a ancora, entao aceitamos e ignoramos a prop para nao
 * precisar tocar nos call sites.
 *
 * Alias em vite.config.ts (resolve.alias['next/link']).
 */
import * as React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

type Props = {
  href: string | { pathname?: string; query?: Record<string, any> };
  as?: any;
  passHref?: boolean;
  replace?: boolean;
  scroll?: boolean;
  shallow?: boolean;
  prefetch?: boolean;
  locale?: any;
  legacyBehavior?: boolean;
  children?: React.ReactNode;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;

function hrefParaString(href: Props['href']): string {
  if (typeof href === 'string') return href;
  const base = href?.pathname ?? '';
  const q = href?.query
    ? '?' + new URLSearchParams(Object.entries(href.query).map(([k, v]) => [k, String(v)])).toString()
    : '';
  return `${base}${q}`;
}

const Link = React.forwardRef<HTMLAnchorElement, Props>(
  ({ href, as, passHref, scroll, shallow, prefetch, locale, legacyBehavior, replace, children, ...rest }, ref) => {
    const navigate = useNavigate();
    const destino = hrefParaString(href);

    // COMPORTAMENTO LEGADO (passHref / legacyBehavior):
    // No Next, com passHref o proprio <Link> NAO renderiza uma ancora - ele
    // repassa o href ao filho, que costuma ser um componente que ja renderiza
    // <a> (Link/Button do MUI). Se renderizassemos a ancora do react-router,
    // teriamos <a> dentro de <a>: HTML invalido, warning do React
    // validateDOMNesting e comportamento de clique imprevisivel.
    //
    // Aqui clonamos o filho passando href + onClick. O onClick navega pelo
    // router (preservando SPA, sem reload) e preserva o onClick original do
    // filho, alem de respeitar ctrl/cmd/clique do meio e target="_blank".
    // DEFEITO CORRIGIDO (18/08/2026): esta linha era
    //
    //   React.Children.count(children) === 1 ? React.Children.only(children) : null
    //
    // e derrubava o APP INTEIRO com "React.Children.only expected to receive a
    // single React element child".
    //
    // A causa: `count` e `only` nao concordam sobre o que e "um filho". `count`
    // ACHATA arrays, entao `{[<a/>]}` conta 1; `only` EXIGE um elemento e lanca se
    // receber array, mesmo de um item. Todo `<NextLink>{arrayDeUmItem}</NextLink>`
    // caia nessa fenda - e como a linha rodava ANTES da checagem de passHref, o
    // estouro acontecia em qualquer NextLink, nao so nos legados.
    //
    // Pior: erro lancado no render de um componente sem error boundary acima
    // desmonta a arvore toda. A tela ficava BRANCA, com a exceção so no console -
    // nada indicava que o culpado era um link.
    //
    // `toArray` resolve os dois lados: achata igual ao `count` e devolve elementos
    // de verdade, sem lancar. Ele acrescenta `key` aos filhos, o que e inofensivo
    // aqui porque o cloneElement abaixo preserva a key.
    const filhos = React.Children.toArray(children);
    const filhoUnico = filhos.length === 1 && React.isValidElement(filhos[0]) ? filhos[0] : null;

    if ((passHref || legacyBehavior) && React.isValidElement(filhoUnico)) {
      const propsFilho: any = filhoUnico.props ?? {};
      return React.cloneElement(filhoUnico as React.ReactElement<any>, {
        href: destino,
        onClick: (evento: React.MouseEvent<HTMLAnchorElement>) => {
          propsFilho.onClick?.(evento);
          if (evento.defaultPrevented) return;
          // Deixa o navegador cuidar de nova aba / nova janela / download.
          if (
            evento.metaKey ||
            evento.ctrlKey ||
            evento.shiftKey ||
            evento.altKey ||
            evento.button !== 0 ||
            (propsFilho.target && propsFilho.target !== '_self')
          )
            return;
          evento.preventDefault();
          navigate(destino, { replace });
        },
      });
    }

    return (
      <RouterLink ref={ref} to={destino} replace={replace} {...rest}>
        {children}
      </RouterLink>
    );
  }
);
Link.displayName = 'NextLinkCompat';

export default Link;
